import Layout from "@/components/layout/Layout";
import React, {
	useState,
	useEffect
} from "react";
import Sidepanel from "@/components/layout/Sidepanel";
import Content from "@/components/layout/Content";
import ContentOptions from "@/components/layout/ContentOptions";
import Heading from "@/components/elements/Heading";
import CutOutInputList from "@/components/elements/CutOutInputList";
import CutOutCanvasController from "@/components/canvas/cutOutShapes/CutOutCanvasController";
import { Inertia } from "@inertiajs/inertia";
import api from "@/services/api";
import CutOffInputList from "@/components/elements/CutOffInputList";
import NavigationButtons from "@/components/elements/Buttons";

/**
 * Cut-outs configurator page
 *
 * @returns {JSX.Element} The rendered cut-outs configurator page
 */
export default function CutOutsConfigurator({
    session,
    product,
	variation,
    step,
    steps,
    heading,
    subheading,
    saveCutOutUrl,
    saveCutOffUrl,
    uploadDrawingUrl,
    shoppingurl,
    prevLink,
}) {
    const [stepList, setStepList] = useState(steps);
    const [plateDimensions] = useState({ width: variation.properties.BREEDTE, height: variation.properties.HOOGTE }); // mm
    const [shapes, setShapes] = useState(variation.cut_outs ?? []);
    const [selectedShapeId, setSelectedShapeId] = useState(null);
    const [selectedTab, setSelectedTab] = useState("sparingen");
    const [productInfo, setProductInfo] = useState(null);
    const [focusedRadiusField, setFocusedRadiusField] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    // Add cutoff state management
    const [cutoffProperties, setCutoffProperties] = useState({
        type: variation.cut_off || "geen",
        pos_tip_left: (variation.cut_off_properties && variation.cut_off_properties.pos_tip_left) || 0,
        pos_right_bottom: (variation.cut_off_properties && variation.cut_off_properties.pos_right_bottom) || 0,
        pos_left_bottom: (variation.cut_off_properties && variation.cut_off_properties.pos_left_bottom) || 0,
        curve_right: (variation.cut_off_properties && variation.cut_off_properties.curve_right) || 0,
        curve_left: (variation.cut_off_properties && variation.cut_off_properties.curve_left) || 0,
    });


    // Fetch product information on component mount
    useEffect(() => {
        const fetchProductInfo = async () => {
            try {
                const products = await api.get(`/api/products/cut-out`).then(response => response.data.products)
                setProductInfo(products);
            } catch (error) {
                console.error('Failed to fetch product information:', error);
            }
        };

        fetchProductInfo();
    }, []);

    // Save the shapes to the server with a debounce
    useEffect(() => {
        setIsSaving(true);
        const timeoutId = setTimeout(() => {

            api.put(saveCutOutUrl, { cut_outs: shapes });
            setIsSaving(false);
        }, 500);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [shapes]);

    // Save the cutoff properties to the server with a debounce
    useEffect(() => {
        setIsSaving(true);
        const timeoutId = setTimeout(() => {

            // Set hardcoded shape type for now.
            cutoffProperties.shape = "POINTED"
            api.put(saveCutOffUrl, cutoffProperties);
            setIsSaving(false);
        }, 500);
        return () => {
            clearTimeout(timeoutId);
        };
    }, [cutoffProperties]);


    // callback to add a shape
    function addShape(shape) {

        setShapes((prev) => [...prev, shape]);

        // Automatically select the newly added shape
        setSelectedShapeId(shape.identifier);
    }

    // callback to update an existing shape
    function updateShape(updatedShape) {
        setShapes((prev) =>
            prev.map((shape) =>
                shape.identifier === updatedShape.identifier
                    ? { ...shape, ...updatedShape }
                    : shape
            )
        );
    }

    function handleDeleteShape(shapeId) {
        setShapes((prev) => prev.filter(shape => shape.identifier !== shapeId));
        regenerateAllIdentifiers();
        setSelectedShapeId(null);
    }

    const regenerateAllIdentifiers = () => {
        setShapes((prev) =>
            prev.map((shape, index) => ({
                ...shape,
                identifier: String.fromCharCode(65 + index)
            }))
        );
    }

    // Handle cutoff property updates
    function handleCutoffPropertyUpdate(property) {
            return (updatedProps) => {
                // Use functional state update to handle single or multiple properties
                setCutoffProperties(prev => ({
                    ...prev,
                    ...updatedProps
                }));
            };
        }

    // Handle shape selection from the list
    const handleSelectShape = (shapeId) => {
        setSelectedShapeId(shapeId);
    };

    function handleTabChange(tab) {
        setSelectedTab(tab);
    }

    /**
     * Basic validation function
     */
    function validateInput() {
        // For now, always return true since we're just displaying the plate
        return true;
    }

    /**
     * Handle step navigation with validation
     */
    function clickStep(e, link) {
        e.preventDefault();
        if (validateInput()) {
            Inertia.visit(link);
        }
    }

    /**
     * Handle shopping cart navigation with validation
     */
    function clickShopping() {
        return validateInput();
    }


    /**
     * Handle focused radius field change from CutOffInputList
     * @param {string|null} fieldName - The focused radius field name or null if no field is focused
     */
    function handleFocusedRadiusFieldChange(fieldName) {
        setFocusedRadiusField(fieldName);
    }

    /**
     * Handle previous step navigation
     */
    function handlePrev() {
        Inertia.visit(prevLink);
    }

    return (
        <Layout>
            <Sidepanel
                steps={stepList}
                step={step}
                validates
                product={product}
                clickStep={clickStep}
            />
            <Content
                steps={stepList}
                step={step}
                shoppingurl={shoppingurl}
                session={session}
                product={product}
                validates
                clickShopping={clickShopping}
                isSaving={isSaving}
            >

                <div className="col-md-6 d-flex justify-content-center align-items-start">
                    <CutOutCanvasController
                        plateDimensions={plateDimensions}
                        shapes={shapes}
                        selectedShapeId={selectedShapeId}
                        onSelectShape={handleSelectShape}
                        cutoffProperties={cutoffProperties}
                        showGuidelines={selectedTab === "plaatopties"}
                        focusedRadiusField={focusedRadiusField}
                    />
                </div>
                <ContentOptions>
                    <Heading heading={heading} subheading={subheading} />
                    <div className="d-flex align-items-start mb-3">
                        <button className={`mr-1 btn ${selectedTab === "sparingen" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => handleTabChange("sparingen")}>Sparingen</button>
                        <button className={`btn ${selectedTab === "plaatopties" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => handleTabChange("plaatopties")}>Plaat opties</button>
                    </div>
                    {selectedTab === "sparingen" && (
                        <CutOutInputList
                            shapes={shapes}
                            addShape={addShape}
                            plateDimensions={plateDimensions}
                            updateShape={updateShape}
                            selectedShapeId={selectedShapeId}
                            onSelectShape={handleSelectShape}
                            productInfo={productInfo}
                            onDeleteShape={handleDeleteShape}
							uploadDrawingUrl={uploadDrawingUrl}
                        />
                    ) }
                    {selectedTab === "plaatopties" && (
                        <CutOffInputList
                            properties={cutoffProperties}
                            handlePropertyUpdate={handleCutoffPropertyUpdate}
                            plateDimensions={plateDimensions}
                            onFocusedRadiusFieldChange={handleFocusedRadiusFieldChange}
                        />
                    )}

                    <NavigationButtons
                        prevlink={prevLink}
                        prevtext={'Opslaan en sluiten'}
                    />
                </ContentOptions>

            </Content>
        </Layout>
    );
}
